/**
 * Modal para impressão de etiquetas de lotes
 * 
 * Permite ao usuário escolher a quantidade de etiquetas a imprimir
 * com valor padrão sendo a quantidade de tambores do lote
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, Button, Space, Descriptions } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { formatDateBR } from '../../helpers/date';

const PrintLabelsModal = ({ visible, onCancel, onPrint, batchInfo }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Atualizar quantidade padrão quando o lote mudar
  useEffect(() => {
    if (visible && batchInfo) {
      form.setFieldsValue({
        quantity: batchInfo.drum_quantity || 1
      });
    }
  }, [visible, batchInfo, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onPrint(values.quantity);
      form.resetFields();
      onCancel();
    } catch (error) {
      // Erro já tratado no onPrint
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={
        <Space>
          <PrinterOutlined />
          Imprimir Etiquetas
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={500}
    >
      {batchInfo && (
        <div style={{ marginBottom: 20 }}>
          <Descriptions 
            bordered 
            size="small" 
            column={1}
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="Lote ID">
              #{batchInfo.id}
            </Descriptions.Item>
            <Descriptions.Item label="Cliente">
              {batchInfo.client?.company_name || 'N/A'}
              {batchInfo.client?.nickname && (
                <div style={{ fontSize: 12, color: '#666' }}>
                  ({batchInfo.client.nickname})
                </div>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Data">
              {formatDateBR(batchInfo.date)}
            </Descriptions.Item>
            {batchInfo.invoice_number && (
              <Descriptions.Item label="Nota Fiscal">
                {batchInfo.invoice_number}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Qtd. Tambores">
              <strong>{batchInfo.drum_quantity || 0}</strong> tambor(es)
            </Descriptions.Item>
          </Descriptions>

          <div style={{ 
            padding: 12, 
            background: '#e6f7ff', 
            borderRadius: 4,
            border: '1px solid #91d5ff'
          }}>
            <div style={{ fontSize: 12, color: '#0050b3' }}>
              💡 <strong>Dica:</strong> A quantidade padrão é igual ao número de tambores do lote. 
              Cada etiqueta pode ser colada em um tambor para rastreamento individual.
            </div>
          </div>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ quantity: batchInfo?.drum_quantity || 1 }}
      >
        <Form.Item
          name="quantity"
          label="Quantidade de Etiquetas"
          rules={[
            { required: true, message: 'Campo obrigatório' },
            { type: 'number', min: 1, max: 100, message: 'Entre 1 e 100 etiquetas' }
          ]}
          extra="Quantidade de etiquetas a serem impressas (máximo 100)"
        >
          <InputNumber
            min={1}
            max={100}
            style={{ width: '100%' }}
            placeholder="Digite a quantidade"
            size="large"
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
          <Space style={{ float: 'right' }}>
            <Button onClick={handleCancel} size="large">
              Cancelar
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading} 
              icon={<PrinterOutlined />}
              size="large"
            >
              Imprimir
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PrintLabelsModal;

